import api from 'mastodon/api';
import { destroyE2ee } from 'mastodon/features/direct_messages/e2ee';

export interface LogOutOptions {
  /**
   * Erase this browser's E2EE key material as part of signing out.
   *
   * OFF by default, and that default is the whole point. C8 originally wiped
   * on EVERY logout so that the next person at a shared machine could not
   * inherit decrypt capability. The cost was catastrophic and hit everyone:
   * the wipe destroys `myDeviceId` / `identity-record` / backup creds, so the
   * next login on the SAME browser looks like a brand-new device, mints a new
   * device id, and silently orphans every message ever sealed to the old one.
   * Reported on kanban #15, where one account minted four devices in two hours
   * — one per login — losing its history each time.
   *
   * The protection is preserved as a deliberate choice instead of a hidden
   * default: the logout confirmation offers "erase encrypted messages on this
   * device", which sets this flag. Threat model for the new default: the E2EE
   * store is per-account (`mastodon-e2ee-<accountId>`), so a DIFFERENT account
   * signing in on this browser never opens it; the residual exposure is local
   * extraction of the browser profile, which is what the opt-in covers.
   */
  wipeE2ee?: boolean;
}

export async function logOut({ wipeE2ee = false }: LogOutOptions = {}) {
  if (wipeE2ee) {
    // C8: wipe ALL local E2EE key material (Signal identity, sessions, sender
    // keys, plaintext caches, e2ee_* localStorage) BEFORE signing out.
    // Failure must not block logout (a half-broken store shouldn't trap the
    // user in a session), so it's best-effort.
    try {
      await destroyE2ee();
    } catch (err) {
      console.error('[E2EE] logout wipe failed (continuing sign-out)', err);
    }
  }

  try {
    const response = await api(false).delete<{ redirect_to?: string }>(
      '/auth/sign_out',
      { headers: { Accept: 'application/json' }, withCredentials: true },
    );

    if (response.status === 200 && response.data.redirect_to)
      window.location.href = response.data.redirect_to;
    else
      console.error(
        'Failed to log out, got an unexpected non-redirect response from the server',
        response,
      );
  } catch (error) {
    console.error('Failed to log out, response was an error', error);
  }
}
